<template>
  <div class="vp-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.video_poker.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.video_poker.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <template v-else>
      <!-- Variant selector tabs -->
      <div class="variant-tabs">
        <button
          v-for="v in variants"
          :key="v.variant"
          class="variant-tab"
          :class="{ active: selectedVariant === v.variant, inactive: !v.is_active }"
          @click="selectVariant(v.variant)"
        >
          {{ shortName(v.variant) }}
          <span v-if="!v.is_active" class="inactive-badge">{{ $t('admin.config.video_poker.badge_off') }}</span>
        </button>
      </div>

      <form v-if="form" class="settings-form" @submit.prevent="saveConfig">
        <!-- Variant info bar -->
        <div class="variant-info-bar">
          <span class="variant-name">{{ form.display_name }}</span>
          <span class="source-badge" :class="form.source === 'database' ? 'badge-saved' : 'badge-default'">
            {{ form.source === 'database' ? $t('admin.config.saved_to_db') : $t('admin.config.using_defaults') }}
          </span>
        </div>

        <!-- RTP display -->
        <div class="rtp-display">
          <div class="rtp-row">
            <span class="rtp-label">{{ $t('admin.config.video_poker.label_rtp_1coin') }}</span>
            <span class="rtp-value">{{ rtp1CoinDisplay }}%</span>
          </div>
          <div class="rtp-row">
            <span class="rtp-label">{{ $t('admin.config.video_poker.label_rtp_max') }}</span>
            <span class="rtp-value highlight">{{ rtpMaxDisplay }}%</span>
          </div>
          <div class="rtp-row">
            <span class="rtp-label">{{ $t('admin.config.video_poker.label_house_edge') }}</span>
            <span class="rtp-value edge">{{ houseEdgeDisplay }}%</span>
          </div>
        </div>

        <!-- Paytable editor -->
        <section class="settings-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('admin.config.video_poker.section_paytable') }}</h3>
            <div class="preset-buttons">
              <button
                v-for="preset in presetsForVariant"
                :key="preset.name"
                type="button"
                class="preset-btn"
                @click="loadPreset(preset)"
              >
                {{ preset.name }}
              </button>
            </div>
          </div>

          <p class="form-hint" style="margin-bottom: 12px;">
            {{ $t('admin.config.video_poker.hint_paytable') }}
          </p>

          <div class="paytable-grid">
            <div class="paytable-header">
              <span class="hand-col">{{ $t('admin.config.video_poker.label_hand') }}</span>
              <span v-for="c in 5" :key="c" class="coin-col">{{ $t('admin.config.video_poker.label_coin_count', { count: c }) }}</span>
            </div>
            <div
              v-for="rank in handRanks"
              :key="rank"
              class="paytable-row"
              :class="{ 'rf-row': rank === 'royal_flush' }"
            >
              <span class="hand-col hand-label">{{ handLabels[rank] ?? rank }}</span>
              <input
                v-for="(_, idx) in 5"
                :key="idx"
                v-model.number="form.paytable[rank][idx]"
                type="number"
                min="0"
                max="99999"
                class="coin-input"
                :class="{ 'bonus-input': rank === 'royal_flush' && idx === 4 }"
                @change="recomputeRtp"
              />
            </div>
          </div>
        </section>

        <!-- Coin Settings -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.video_poker.section_coin_settings') }}</h3>

          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.config.video_poker.label_max_coins') }}
              <div class="radio-group">
                <label v-for="c in [3, 4, 5]" :key="c" class="radio-label">
                  <input v-model.number="form.max_coins" type="radio" :value="c" class="radio-input" />
                  <span>{{ $t('admin.config.video_poker.label_coin_count', { count: c }) }}</span>
                </label>
              </div>
              <span class="form-hint">{{ $t('admin.config.video_poker.hint_max_coins') }}</span>
            </label>

            <label class="form-label">
              {{ $t('admin.config.video_poker.label_coin_denomination') }}
              <input
                v-model.number="form.coin_denomination"
                type="number"
                step="0.25"
                min="0.01"
                max="100"
                class="form-input"
                required
              />
              <span class="form-hint">{{ $t('admin.config.video_poker.hint_coin_denomination') }}</span>
            </label>
          </div>
        </section>

        <!-- Bet Limits -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>

          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.config.label_min_bet') }}
              <input
                v-model.number="form.min_bet"
                type="number"
                step="0.01"
                min="0.01"
                class="form-input"
                required
              />
            </label>

            <label class="form-label">
              {{ $t('admin.config.label_max_bet') }}
              <input
                v-model.number="form.max_bet"
                type="number"
                step="0.01"
                min="0.01"
                class="form-input"
                required
              />
            </label>
          </div>

          <div class="form-row" style="margin-top: 14px;">
            <label class="form-label">
              {{ $t('admin.config.label_max_payout') }}
              <input
                v-model.number="form.max_payout"
                type="number"
                step="0.01"
                min="0"
                class="form-input"
                style="max-width: 240px;"
              />
              <span class="form-hint">{{ $t('admin.config.hint_no_cap') }}</span>
            </label>
          </div>
        </section>

        <!-- Features -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.video_poker.section_features') }}</h3>

          <div class="form-row">
            <label class="toggle-label">
              <input v-model="form.gamble_enabled" type="checkbox" class="toggle-checkbox" />
              <span>
                {{ $t('admin.config.video_poker.label_gamble') }}
                <span v-if="form.gamble_enabled" class="rule-badge badge-info">{{ $t('admin.config.video_poker.badge_enabled') }}</span>
                <span v-else class="rule-badge badge-disabled">{{ $t('admin.config.video_poker.badge_disabled') }}</span>
              </span>
            </label>
          </div>

          <div class="form-row" style="margin-top: 12px;">
            <label class="toggle-label">
              <input v-model="form.multi_hand_enabled" type="checkbox" class="toggle-checkbox" />
              <span>
                {{ $t('admin.config.video_poker.label_multi_hand') }}
                <span v-if="form.multi_hand_enabled" class="rule-badge badge-info">{{ $t('admin.config.video_poker.badge_enabled') }}</span>
                <span v-else class="rule-badge badge-disabled">{{ $t('admin.config.video_poker.badge_disabled') }}</span>
              </span>
            </label>
          </div>
        </section>

        <!-- Active status -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.video_poker.section_availability') }}</h3>

          <div class="form-row">
            <label class="toggle-label">
              <input v-model="form.is_active" type="checkbox" class="toggle-checkbox" />
              <span>
                {{ $t('admin.config.video_poker.label_available_to_players') }}
                <span v-if="form.is_active" class="rule-badge badge-info">{{ $t('admin.config.video_poker.badge_active') }}</span>
                <span v-else class="rule-badge badge-disabled">{{ $t('admin.config.video_poker.badge_inactive') }}</span>
              </span>
            </label>
            <span class="form-hint" style="margin-top: 6px;">
              {{ $t('admin.config.video_poker.hint_availability') }}
            </span>
          </div>
        </section>

        <!-- Save button -->
        <div class="form-actions">
          <button type="submit" class="save-btn" :disabled="saving">
            {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
          </button>
          <span v-if="saveSuccess" class="save-success">{{ $t('admin.config.video_poker.save_success') }}</span>
          <span v-if="saveError" class="save-error">{{ saveError }}</span>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const { t } = useI18n()

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariantForm {
  source: string
  variant: string
  display_name: string
  paytable: Record<string, number[]>
  max_coins: number
  coin_denomination: number
  min_bet: number
  max_bet: number
  max_payout: number
  gamble_enabled: boolean
  multi_hand_enabled: boolean
  is_active: boolean
  rtp_1coin: number
  rtp_max_coins: number
  house_edge: number
}

interface Preset {
  name: string
  paytable: Record<string, number[]>
}

type VariantListItem = VariantForm

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const loading = ref(true)
const loadError = ref('')
const saving = ref(false)
const saveSuccess = ref(false)
const saveError = ref('')

const variants = ref<VariantListItem[]>([])
const handRanks = ref<string[]>([])
const handLabels = ref<Record<string, string>>({})
const selectedVariant = ref('jacks_or_better')
const form = ref<VariantForm | null>(null)

// Local RTP state (updated when paytable cells change)
const localRtp1Coin = ref(0)
const localRtpMaxCoins = ref(0)

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const rtp1CoinDisplay = computed(() => localRtp1Coin.value.toFixed(2))
const rtpMaxDisplay = computed(() => localRtpMaxCoins.value.toFixed(2))
const houseEdgeDisplay = computed(() => (100 - localRtpMaxCoins.value).toFixed(2))

/** Presets for the currently selected variant. */
const presetsForVariant = computed((): Preset[] => {
  const v = selectedVariant.value
  const presets: Preset[] = []

  if (v === 'jacks_or_better') {
    presets.push({
      name: 'JoB 9/6 (Full Pay)',
      paytable: {
        royal_flush: [250, 500, 750, 1000, 4000],
        straight_flush: [50, 100, 150, 200, 250],
        four_of_a_kind: [25, 50, 75, 100, 125],
        full_house: [9, 18, 27, 36, 45],
        flush: [6, 12, 18, 24, 30],
        straight: [4, 8, 12, 16, 20],
        three_of_a_kind: [3, 6, 9, 12, 15],
        two_pair: [2, 4, 6, 8, 10],
        jacks_or_better: [1, 2, 3, 4, 5],
        pair_of_tens: [0, 0, 0, 0, 0],
        nothing: [0, 0, 0, 0, 0],
      },
    })
    presets.push({
      name: 'JoB 8/5 (Casino Pay)',
      paytable: {
        royal_flush: [250, 500, 750, 1000, 4000],
        straight_flush: [50, 100, 150, 200, 250],
        four_of_a_kind: [25, 50, 75, 100, 125],
        full_house: [8, 16, 24, 32, 40],
        flush: [5, 10, 15, 20, 25],
        straight: [4, 8, 12, 16, 20],
        three_of_a_kind: [3, 6, 9, 12, 15],
        two_pair: [2, 4, 6, 8, 10],
        jacks_or_better: [1, 2, 3, 4, 5],
        pair_of_tens: [0, 0, 0, 0, 0],
        nothing: [0, 0, 0, 0, 0],
      },
    })
  }

  if (v === 'deuces_wild') {
    presets.push({
      name: 'DW Casino Pay (~97%)',
      paytable: {
        royal_flush: [250, 500, 750, 1000, 4000],
        straight_flush: [9, 18, 27, 36, 45],
        four_of_a_kind: [5, 10, 15, 20, 25],
        full_house: [3, 6, 9, 12, 15],
        flush: [2, 4, 6, 8, 10],
        straight: [2, 4, 6, 8, 10],
        three_of_a_kind: [1, 2, 3, 4, 5],
        two_pair: [0, 0, 0, 0, 0],
        jacks_or_better: [0, 0, 0, 0, 0],
        pair_of_tens: [0, 0, 0, 0, 0],
        nothing: [0, 0, 0, 0, 0],
      },
    })
  }

  if (v === 'tens_or_better') {
    presets.push({
      name: 'ToB Full Pay (~99%)',
      paytable: {
        royal_flush: [250, 500, 750, 1000, 4000],
        straight_flush: [50, 100, 150, 200, 250],
        four_of_a_kind: [25, 50, 75, 100, 125],
        full_house: [6, 12, 18, 24, 30],
        flush: [5, 10, 15, 20, 25],
        straight: [4, 8, 12, 16, 20],
        three_of_a_kind: [3, 6, 9, 12, 15],
        two_pair: [2, 4, 6, 8, 10],
        jacks_or_better: [1, 2, 3, 4, 5],
        pair_of_tens: [1, 2, 3, 4, 5],
        nothing: [0, 0, 0, 0, 0],
      },
    })
  }

  if (v === 'double_bonus') {
    presets.push({
      name: 'Double Bonus (~100%)',
      paytable: {
        royal_flush: [250, 500, 750, 1000, 4000],
        straight_flush: [50, 100, 150, 200, 250],
        four_of_a_kind: [80, 160, 240, 320, 400],
        full_house: [9, 18, 27, 36, 45],
        flush: [6, 12, 18, 24, 30],
        straight: [5, 10, 15, 20, 25],
        three_of_a_kind: [3, 6, 9, 12, 15],
        two_pair: [1, 2, 3, 4, 5],
        jacks_or_better: [1, 2, 3, 4, 5],
        pair_of_tens: [0, 0, 0, 0, 0],
        nothing: [0, 0, 0, 0, 0],
      },
    })
  }

  return presets
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shortName(variant: string): string {
  const names: Record<string, string> = {
    jacks_or_better: 'JoB',
    deuces_wild: 'Deuces',
    tens_or_better: 'ToB',
    bonus_poker: 'Bonus',
    double_bonus: 'D-Bonus',
    aces_and_faces: 'Aces+F',
    joker_poker: 'Joker',
  }
  return names[variant] ?? variant
}

/** Approximate RTP calculation from paytable using JoB hand frequencies. */
function calcRtp(paytable: Record<string, number[]>, coinIdx: number): number {
  const JOB_FREQS: Record<string, number> = {
    royal_flush: 0.0000247,
    straight_flush: 0.000109,
    four_of_a_kind: 0.00236,
    full_house: 0.01151,
    flush: 0.01101,
    straight: 0.01123,
    three_of_a_kind: 0.07445,
    two_pair: 0.12928,
    jacks_or_better: 0.21459,
    pair_of_tens: 0.0,
    nothing: 0.54544,
  }
  const coins = coinIdx + 1
  let ev = 0
  for (const [hand, freq] of Object.entries(JOB_FREQS)) {
    const coinsReturned = paytable[hand]?.[coinIdx] ?? 0
    ev += freq * coinsReturned
  }
  return (ev / coins) * 100
}

function recomputeRtp(): void {
  if (!form.value) return
  const coinIdx = (form.value.max_coins ?? 5) - 1
  localRtp1Coin.value = calcRtp(form.value.paytable, 0)
  localRtpMaxCoins.value = calcRtp(form.value.paytable, coinIdx)
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function loadConfig(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get('/admin/api/games/video-poker/config')
    const data = res.data as {
      variants: VariantListItem[]
      hand_ranks: string[]
      hand_labels: Record<string, string>
    }
    variants.value = data.variants
    handRanks.value = data.hand_ranks
    handLabels.value = data.hand_labels
    applyVariant(selectedVariant.value)
  } catch {
    loadError.value = t('admin.config.video_poker.error_load')
  } finally {
    loading.value = false
  }
}

function applyVariant(variant: string): void {
  const v = variants.value.find((x) => x.variant === variant)
  if (!v) return
  // Deep-copy to avoid mutating the variants list
  form.value = JSON.parse(JSON.stringify(v)) as VariantForm
  localRtp1Coin.value = v.rtp_1coin
  localRtpMaxCoins.value = v.rtp_max_coins
}

function selectVariant(variant: string): void {
  selectedVariant.value = variant
  applyVariant(variant)
  saveSuccess.value = false
  saveError.value = ''
}

function loadPreset(preset: Preset): void {
  if (!form.value) return
  form.value.paytable = JSON.parse(JSON.stringify(preset.paytable)) as Record<string, number[]>
  recomputeRtp()
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

async function saveConfig(): Promise<void> {
  if (!form.value) return
  saving.value = true
  saveSuccess.value = false
  saveError.value = ''

  try {
    const res = await apiClient.post('/admin/api/games/video-poker/config', {
      variant: form.value.variant,
      paytable: form.value.paytable,
      max_coins: form.value.max_coins,
      coin_denomination: form.value.coin_denomination,
      min_bet: form.value.min_bet,
      max_bet: form.value.max_bet,
      max_payout: form.value.max_payout,
      gamble_enabled: form.value.gamble_enabled,
      multi_hand_enabled: form.value.multi_hand_enabled,
      is_active: form.value.is_active,
    })

    const saved = res.data as VariantForm

    // Update the variants list entry
    const idx = variants.value.findIndex((v) => v.variant === saved.variant)
    if (idx >= 0) variants.value[idx] = saved

    form.value = JSON.parse(JSON.stringify(saved)) as VariantForm
    localRtp1Coin.value = saved.rtp_1coin
    localRtpMaxCoins.value = saved.rtp_max_coins

    saveSuccess.value = true
    setTimeout(() => {
      saveSuccess.value = false
    }, 3000)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.video_poker.error_save')
  } finally {
    saving.value = false
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(loadConfig)

watch(
  () => form.value?.max_coins,
  () => recomputeRtp(),
)
</script>

<style scoped>
.vp-admin-config {
  max-width: 900px;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #9ca3af);
  font-size: 0.875rem;
  margin: 0;
}

.loading-state {
  color: var(--text-secondary, #9ca3af);
  padding: 24px 0;
}

.error-banner {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--color-danger, #ef4444);
  margin-bottom: 16px;
}

/* Variant tabs */
.variant-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.variant-tab {
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--border-color, rgba(255,255,255,0.1));
  background: var(--bg-card, rgba(255,255,255,0.05));
  color: var(--text-secondary, #9ca3af);
  transition: all 0.15s;
  position: relative;
}

.variant-tab.active {
  background: var(--accent-color, #6c47ff);
  color: var(--color-on-dark, #fff);
  border-color: transparent;
}

.variant-tab:hover:not(.active) {
  background: var(--bg-card-hover, rgba(255,255,255,0.09));
  color: var(--text-primary, #fff);
}

.inactive-badge {
  font-size: 0.65rem;
  background: rgba(107, 114, 128, 0.3);
  color: var(--color-muted, #9ca3af);
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 4px;
}

/* Variant info bar */
.variant-info-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.variant-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.source-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-saved {
  background: rgba(16, 185, 129, 0.15);
  color: var(--color-success-mid, #10b981);
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.badge-default {
  background: rgba(107, 114, 128, 0.15);
  color: var(--color-muted, #9ca3af);
  border: 1px solid rgba(107, 114, 128, 0.3);
}

/* RTP display */
.rtp-display {
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  background: var(--bg-card, rgba(255,255,255,0.04));
  border: 1px solid var(--border-color, rgba(255,255,255,0.08));
  border-radius: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.rtp-row {
  display: flex;
  flex-direction: column;
  min-width: 120px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #9ca3af);
  margin-bottom: 2px;
}

.rtp-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.rtp-value.highlight {
  color: var(--color-success-mid, #10b981);
}

.rtp-value.edge {
  color: var(--color-warning, #f59e0b);
}

/* Settings sections */
.settings-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.settings-section {
  padding: 20px 0;
  border-bottom: 1px solid var(--border-color, rgba(255,255,255,0.07));
}

.settings-section:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 12px;
}

.section-header .section-title {
  margin-bottom: 0;
}

/* Preset buttons */
.preset-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--accent-color, rgba(108, 71, 255, 0.5));
  background: rgba(108, 71, 255, 0.1);
  color: var(--accent-color, #6c47ff);
  transition: all 0.15s;
}

.preset-btn:hover {
  background: rgba(108, 71, 255, 0.2);
}

/* Paytable grid */
.paytable-grid {
  border: 1px solid var(--border-color, rgba(255,255,255,0.1));
  border-radius: 8px;
  overflow: hidden;
}

.paytable-header,
.paytable-row {
  display: grid;
  grid-template-columns: 160px repeat(5, 1fr);
  align-items: center;
}

.paytable-header {
  background: var(--bg-card-hover, rgba(255,255,255,0.07));
  padding: 8px 12px;
}

.paytable-row {
  padding: 4px 12px;
  border-top: 1px solid var(--border-color, rgba(255,255,255,0.05));
}

.paytable-row.rf-row {
  background: rgba(251, 191, 36, 0.04);
}

.hand-col {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary, #9ca3af);
}

.hand-label {
  color: var(--text-primary, #fff);
}

.coin-col {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary, #9ca3af);
  text-align: center;
}

.coin-input {
  width: 100%;
  max-width: 72px;
  margin: 3px 4px 3px 0;
  padding: 5px 6px;
  border-radius: 5px;
  border: 1px solid var(--border-color, rgba(255,255,255,0.12));
  background: var(--bg-input, rgba(255,255,255,0.05));
  color: var(--text-primary, #fff);
  font-size: 0.85rem;
  text-align: center;
}

.coin-input.bonus-input {
  border-color: rgba(251, 191, 36, 0.5);
  color: var(--color-warning-light, #fbbf24);
}

.coin-input:focus {
  outline: none;
  border-color: var(--accent-color, #6c47ff);
}

/* Form elements */
.form-row {
  display: flex;
  flex-direction: column;
}

.form-row.two-col {
  flex-direction: row;
  gap: 24px;
  flex-wrap: wrap;
}

.form-row.two-col .form-label {
  flex: 1;
  min-width: 200px;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary, #9ca3af);
}

.form-input,
.form-select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color, rgba(255,255,255,0.12));
  background: var(--bg-input, rgba(255,255,255,0.05));
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
  max-width: 240px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--accent-color, #6c47ff);
}

.form-hint {
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
}

.radio-group {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
}

.radio-input {
  accent-color: var(--accent-color, #6c47ff);
}

.toggle-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
}

.toggle-checkbox {
  accent-color: var(--accent-color, #6c47ff);
  margin-top: 2px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.rule-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 6px;
}

.badge-info {
  background: rgba(59, 130, 246, 0.15);
  color: var(--color-info-light, #60a5fa);
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.badge-disabled {
  background: rgba(107, 114, 128, 0.15);
  color: var(--color-muted, #9ca3af);
  border: 1px solid rgba(107, 114, 128, 0.3);
}

/* Form actions */
.form-actions {
  padding-top: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.save-btn {
  padding: 10px 28px;
  border-radius: 8px;
  background: var(--accent-color, #6c47ff);
  color: var(--color-on-dark, #fff);
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-btn:not(:disabled):hover {
  opacity: 0.9;
}

.save-success {
  color: var(--color-success-mid, #10b981);
  font-size: 0.875rem;
  font-weight: 500;
}

.save-error {
  color: var(--color-danger, #ef4444);
  font-size: 0.875rem;
}
</style>
